import * as Sentry from '@sentry/react'
import moment from 'moment'
import PropTypes from 'prop-types'
import queryString from 'query-string'
import { createContext, useCallback, useMemo, useState } from 'react'
import { useEffectOnce } from 'react-use'
import store from 'store'

import agent from 'agent'
import RBACFallback from 'rbac.json'
import { resetAnalytics } from 'track'
import { PricingPlanTier } from 'types/api/companies/contracts'
import { ContractLimitItemKeys } from 'types/api/user/session'
import { ms } from 'utils/millisecond'
import { isPlainObject } from 'utils/obj'
import { getSessionData, NO_TEAM } from 'utils/session'

export { NO_TEAM }

const BASE_SESSION = {
  company_id: '',
  company_name: '',
  company_alias: '',
  company_is_alfi_enabled: false,
  roles: [],
  privileges: [],
  expires_at: '',
  header: '',
  identifier: '',
  first_name: '',
  last_name: '',
  teams: [],
  last_login: '',
  renew_token: '',
  token: '',
  tier: 'Enterprise',
  company_contract_expires_at: moment().add(8, 'days').toDate(),
  prefs: {},
  has_fault_injection: false,
  has_reliability_management: false,
  limits: {},
  has_failure_flags: false,
  is_trial: false,
  paywall_status: {},
  change_requested: false // TODO part of free/upgrade - needs to be removed, but still used
}

export const BASE_PAYMENT_INFO = {
  invoiceDate: null,
  amountInCents: null,
  servicesConsumed: null
}

export const DEFAULT_AUTH_VALUES = {
  RBAC: {},
  authHeader: '',
  companyDisplayName: '',
  companyId: '',
  companyIsAlfiEnabled: false,
  companyPrefs: {},
  companyPrivileges: [],
  contractExpirationImminent: false,
  currentTeam: {},
  currentTeamId: '',
  currentTeamRoles: [],
  daysUntilContractExpiration: 99,
  endSession: () => {},
  fetchSession: () => {},
  getTeamDetails: () => {},
  hasCompanyPrivilege: () => {},
  hasElevatedCompanyRole: false,
  hasElevatedRole: false,
  hasElevatedTeamRole: false,
  hasReliabilityManagement: false,
  hasFaultInjection: false,
  hasFailureFlags: false,
  hasTeamPrivilege: () => {},
  hasUpgradeBeenRequested: false,
  isAdmin: false,
  isFreeEdition: false,
  isProductionEnvironment: false,
  isRbacEnabled: false,
  isSuper: false,
  privilegesForTeam: [],
  ready: false,
  renewSession: () => {},
  roles: [],
  session: BASE_SESSION,
  startSession: () => {},
  teams: [],
  tier: 'Enterprise',
  limits: {},
  isRMSubscription: false,
  isTrial: false,
  paywallStatus: {},
  isPaywallActive: false,
  setActiveTeamId: () => {},
  paymentInfo: BASE_PAYMENT_INFO
}
export const AuthContext = createContext(DEFAULT_AUTH_VALUES)

async function getRbacSummary(session, teamId) {
  let rbacSummary
  try {
    // do not cache the data from this call
    // the endpoint is fast and returns static data
    // and if it changes, we need it to be updated in the browser asap
    const { data } = await agent.get(
      `/roledefinitions/summary?teamId=${teamId}`,
      {
        headers: {
          Authorization: session.header
        }
      }
    )

    rbacSummary = data

    // cache recently-retrieved data in local storage
    store.set('rbac-summary', {
      date: new Date(),
      summary: rbacSummary
    })
  } catch (e) {
    // use previously cached copy if the api call fails
    const storedSummary = store.get('rbac-summary')
    if (storedSummary) {
      const { date, summary } = storedSummary
      // Only refresh summary if older than 24 hours
      if (date && summary && new Date() - new Date(date) <= ms.days(1)) {
        return summary
      }
    } else {
      // and if we don't have it cached then use our fixture copy of the RBAC data
      rbacSummary = RBACFallback
    }
  }

  return rbacSummary
}

async function getUserSession(session) {
  let res = await agent.get('/users/sessions?getCompanySession=true', {
    headers: {
      Authorization: session.header
    }
  })
  return res.data
}

async function renewUserSession(session) {
  const res = await agent.post(
    '/users/renew/rbac',
    queryString.stringify({
      email: session.identifier,
      companyId: session.company_id,
      renewToken: session.renew_token
    }),
    {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    }
  )
  return res.data
}

export function setActiveTeamIdInLocalStorage(teamId, companyId) {
  const activeTeamIdValue = store.get('lastActiveOrgId')
  if (isPlainObject(activeTeamIdValue)) {
    return store.set('lastActiveOrgId', {
      ...activeTeamIdValue,
      [companyId]: teamId
    })
  } else {
    store.set('lastActiveOrgId', {
      [companyId]: teamId
    })
  }
}

export function getActiveTeamIdFromLocalStorage(companyId) {
  const activeTeamIdValue = store.get('lastActiveOrgId')

  if (typeof activeTeamIdValue === 'string') {
    return activeTeamIdValue
  } else if (isPlainObject(activeTeamIdValue)) {
    return activeTeamIdValue[companyId]
  }
}

export function getDaysUntilContractExpiration(expiration, today) {
  return Math.ceil((new Date(expiration) - today) / 1000 / 60 / 60 / 24)
}

export function AuthProvider({
  initialSession = BASE_SESSION,
  initialReady = false,
  initialCurrentTeamId = '',
  initialIsRbacEnabled = false,
  initialRbac = RBACFallback,
  children
}) {
  const [authState, setAllAuth] = useState(() => ({
    ready: initialReady,
    session: initialSession,
    currentTeamId: initialCurrentTeamId,
    isRbacEnabled: initialIsRbacEnabled,
    RBAC: initialRbac,
    paymentInfo: BASE_PAYMENT_INFO
  }))

  const { paymentInfo, session, ready, currentTeamId, isRbacEnabled, RBAC } =
    authState

  const endSession = useCallback(async () => {
    if (session.header) {
      try {
        await agent.delete('/users/auth')
        /**
         * The following action wipes out all local storage persistence without discretion, whereas the
         *  need to persist the user preference state for alert visibility is needed. This single key
         *  is temporarily stored and re-applied following the clear() call.
         */
        const alertPreferencesPersistence = store.get('alertPreferences')
        const redirectPathPersistence = store.get('loginRedirect')
        // we store some user settings in localStorage to survive page navigations
        // so make sure that's cleared out on logout
        window.localStorage.clear()
        // re-applying the user alert preference object
        store.set('alertPreferences', alertPreferencesPersistence)
        store.set('loginRedirect', redirectPathPersistence)
        // eslint-disable-next-line no-empty
      } catch (err) {}
    }

    // clear segment.com analytics data
    resetAnalytics()
    /**
     * TODO: These calls are potentially redundant if followed by the explicit localStorage.clear() call
     * above, and a new design should be considered to persist local data and delete necessary ones.
     */
    store.remove('session')
    store.remove('onboardingAccessed')
    // discard the choice of resetting secret key when user log out
    store.remove('confirmSecretReset')
    store.remove('pendingEnvironmentRequestID')
    setAllAuth((current) => ({
      ...current,
      ready: true,
      session: BASE_SESSION,
      currentTeamId: '',
      isRbacEnabled: false,
      paymentInfo: BASE_PAYMENT_INFO
    }))
  }, [session.header, setAllAuth])

  const startSession = useCallback(
    async (activeSession = store.get('session'), activeTeamId) => {
      const hasSession = activeSession?.company_id

      activeTeamId =
        activeTeamId ||
        getActiveTeamIdFromLocalStorage(hasSession ?? currentTeamId)

      const getPaymentInfo = async (session) => {
        // don't want this to potentially block the session startup if it fails
        try {
          const res = await agent.get(
            `/payments/next-invoice?teamId=${activeTeamId}`,
            {
              headers: {
                Authorization: session.header
              }
            }
          )
          return res.data
        } catch {
          return null
        }
      }

      if (!hasSession) {
        setAllAuth((current) => ({
          ...current,
          ready: true
        }))
        return
      }

      const isExpired = new Date(activeSession.expires_at) <= new Date()
      let updatedSession = activeSession
      try {
        if (isExpired) {
          updatedSession = await renewUserSession(activeSession)
        } else {
          updatedSession = await getUserSession(activeSession)
        }
      } catch (e) {
        return endSession()
      }

      const { sessionToStart, activeTeam } = getSessionData(
        updatedSession,
        activeTeamId
      )

      if (process.env.NODE_ENV === 'production') {
        Sentry.setUser({
          email: sessionToStart.identifier,
          id: sessionToStart.identifier
        })
      }

      const rbacSummary = await getRbacSummary(sessionToStart, activeTeamId)
      const currentPaymentInfo =
        sessionToStart.tier === PricingPlanTier.RMSubscription &&
        !sessionToStart.is_trial
          ? await getPaymentInfo(sessionToStart)
          : {}

      setAllAuth({
        currentTeamId: activeTeam.identifier,
        isRbacEnabled: true,
        RBAC: rbacSummary,
        ready: true,
        session: sessionToStart,
        paymentInfo: currentPaymentInfo ?? paymentInfo
      })

      store.set('session', sessionToStart)
      setActiveTeamIdInLocalStorage(
        activeTeam.identifier,
        updatedSession.company_id
      )
      return sessionToStart
    },
    [endSession, paymentInfo, currentTeamId]
  )

  const renewSession = useCallback(async () => {
    try {
      const nextSession = await startSession(await renewUserSession(session))
      return nextSession
    } catch (err) {
      endSession()
    }
  }, [endSession, session, startSession])

  const fetchSession = useCallback(async () => {
    try {
      const nextSession = await startSession(await getUserSession(session))
      return nextSession
    } catch (e) {
      endSession()
    }
  }, [endSession, session, startSession])

  const setActiveTeamId = useCallback(
    async (teamId) => {
      setAllAuth((current) => ({
        ...current,
        currentTeamId: teamId
      }))

      setActiveTeamIdInLocalStorage(teamId, session.company_id)
    },
    [session.company_id]
  )

  const getTeamDetails = useCallback(
    function getTeamDetails(teamId) {
      return (
        session.teams.find((t) => t.identifier === teamId) || {
          header: '',
          privileges: [],
          roles: []
        }
      )
    },
    [session.teams]
  )

  const hasCompanyPrivilege = useCallback(
    (privilege) => Boolean(session.privileges.includes(privilege?.key)),
    [session.privileges]
  )

  const privilegesForTeam = useCallback(
    function privilegesForTeam(teamId) {
      return getTeamDetails(teamId).privileges
    },
    [getTeamDetails]
  )

  const hasTeamPrivilege = useCallback(
    (teamId, privilege) =>
      privilegesForTeam(teamId).includes(privilege?.key) ||
      // Some high level roles can manage teams even if not a member
      hasCompanyPrivilege(privilege),
    [hasCompanyPrivilege, privilegesForTeam]
  )

  /**
   * Function that inserts Infinity in place of any missing key passed through the limits
   * object from the sessions response
   * @param sessionLimits
   * @returns {*}
   */
  const resolveLimits = (sessionLimits = {}) => {
    ContractLimitItemKeys.forEach((limitKey) => {
      if (!sessionLimits[limitKey]) {
        sessionLimits[limitKey] = Infinity
      }
    })
    return sessionLimits
  }

  const authContextValue = useMemo(() => {
    const currentTeam = getTeamDetails(currentTeamId)
    const currentRoles = session.roles.concat(currentTeam.roles)
    const daysUntilContractExpiration = getDaysUntilContractExpiration(
      session.company_contract_expires_at,
      new Date()
    )

    const paywallStatusKeysForActivePaywall = [
      'PAYMENT_METHOD_DETACHED_PAYMENT_DUE',
      'PAYMENT_METHOD_INVALID_PAYMENT_DUE',
      'TRIAL_EXPIRED_PAYMENT_METHOD_REQUIRED',
      'PAYMENT_VERIFICATION_PENDING',
      'PAYMENT_PENDING'
    ]

    return {
      RBAC,
      authHeader: session.header,
      companyDisplayName: session.company_alias || session.company_name,
      companyId: session.company_id,
      companyIsAlfiEnabled: session.company_is_alfi_enabled,
      companyPrefs: session.prefs,
      companyPrivileges: session.privileges,
      contractExpirationImminent: daysUntilContractExpiration <= 7,
      currentTeam,
      currentTeamId,
      currentTeamRoles: currentTeam.roles,
      daysUntilContractExpiration: daysUntilContractExpiration,
      endSession,
      fetchSession,
      getTeamDetails,
      hasFaultInjection: session.has_fault_injection,
      hasReliabilityManagement: session.has_reliability_management,
      hasFailureFlags: session.has_failure_flags,
      hasCompanyPrivilege,
      hasElevatedCompanyRole: currentRoles
        .filter((role) => role !== 'DEV' && role !== 'ADMIN')
        .some((role) => role !== RBAC.roles.company.COMPANY_USER.key),
      hasElevatedRole: currentRoles.some(
        (role) => role.includes('MANAGER') || role.includes('ADMIN')
      ),
      hasPaymentCompanyRole: currentRoles.some(
        (role) => role !== RBAC.roles.company.COMPANY_USER.key
      ),
      hasElevatedTeamRole: currentTeam.roles.some(
        (role) => role !== RBAC.roles.team.TEAM_USER.key
      ),
      hasTeamPrivilege,
      hasUpgradeBeenRequested: Boolean(session.change_requested),
      isAdmin: currentRoles.some((role) => role === 'COMPANY_ADMIN'),
      isFreeEdition: session.tier === PricingPlanTier.Free,
      isProductionEnvironment:
        session?.prefs?.production || currentTeam?.production,
      isRMSubscription: session.tier === PricingPlanTier.RMSubscription,
      isRbacEnabled,
      isSuper: currentRoles.some(
        (role) =>
          role === 'COMPANY_ADMIN' ||
          role === 'COMPANY_MANAGER' ||
          role === 'TEAM_MANAGER'
      ),
      privilegesForTeam,
      ready,
      renewSession: renewSession,
      roles: currentRoles,
      session,
      setActiveTeamId,
      startSession: startSession,
      teams: session.teams,
      tier: session.tier,
      isTrial: session.is_trial,
      paywallStatus: session.paywall_status,
      isPaywallActive: Boolean(
        paywallStatusKeysForActivePaywall.includes(session.paywall_status?.key)
      ),
      limits: resolveLimits(session.limits),
      paymentInfo
    }
  }, [
    RBAC,
    currentTeamId,
    endSession,
    fetchSession,
    getTeamDetails,
    hasCompanyPrivilege,
    hasTeamPrivilege,
    isRbacEnabled,
    privilegesForTeam,
    ready,
    renewSession,
    session,
    setActiveTeamId,
    startSession,
    paymentInfo
  ])

  useEffectOnce(() => {
    startSession()
  })

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  )
}

AuthProvider.propTypes = {
  children: PropTypes.node,
  initialCurrentTeamId: PropTypes.string,
  initialIsRbacEnabled: PropTypes.bool,
  initialRbac: PropTypes.object,
  initialReady: PropTypes.bool,
  initialSession: PropTypes.object,
  initialPaymentInfo: PropTypes.object
}
